import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Modal, Box, IconButton } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import './zap-tour.scss';
import { PlanEntitlements } from 'custom-type';

// Screenshots of the goal-first wizard, captured from the ClanConnect mobile app.
import sHome from '../../../../assets/images/zap-tour/screens/01-zap-home.jpeg';
import sGoal from '../../../../assets/images/zap-tour/screens/02-goal.jpeg';
import sGrowth from '../../../../assets/images/zap-tour/screens/04-growth-followers.jpeg';
import sPosts from '../../../../assets/images/zap-tour/screens/07-posts-selected.jpeg';
import sWord from '../../../../assets/images/zap-tour/screens/08-word.jpeg';
import sReply from '../../../../assets/images/zap-tour/screens/09-reply.jpeg';
import sWho from '../../../../assets/images/zap-tour/screens/10-who-followers.jpeg';
import sCard from '../../../../assets/images/zap-tour/screens/12-card.jpeg';
import sMessage from '../../../../assets/images/zap-tour/screens/13-message-link.jpeg';
import sReview from '../../../../assets/images/zap-tour/screens/14-review.jpeg';

interface Props {
    open: boolean;
    onClose: () => void;
    // Still accepted so callers don't change. The simplified guide no longer
    // quotes plan limits — it just says some options need a paid plan.
    entitlements?: PlanEntitlements | null;
}

const NAV = [
    { id: 'how', label: 'How it works' },
    { id: 'setup', label: 'Before you start' },
    { id: 'start', label: 'Start' },
    { id: 'step1', label: 'Step 1' },
    { id: 'step2', label: 'Step 2' },
    { id: 'step3', label: 'Step 3' },
    { id: 'step4', label: 'Step 4' },
    { id: 'done', label: 'Done' },
];

/**
 * The guide itself, without a container. Split out of the modal so the Zap
 * setup wizard can show it too: the wizard is a Radix dialog, which blocks
 * clicks, scroll and focus outside itself, so an MUI modal opened over it would
 * be dead. The wizard wraps this in a nested Radix dialog instead.
 * Expects a positioned parent with a fixed height (it scrolls internally).
 */
export const ZapTourGuide: React.FC<{ onClose: () => void }> = ({ onClose }) => {
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [progress, setProgress] = useState(0);
    const [showToTop, setShowToTop] = useState(false);
    const [activeId, setActiveId] = useState<string>('how');
    const [navOpen, setNavOpen] = useState(false);
    const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null);

    // Progress bar + back-to-top + active-nav highlight, all driven by the modal's own
    // scroll container. Active section is computed deterministically here (an
    // IntersectionObserver was unreliable inside the MUI portal / with lazy-loaded images).
    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const max = el.scrollHeight - el.clientHeight;
        setProgress(max > 0 ? (el.scrollTop / max) * 100 : 0);
        setShowToTop(el.scrollTop > 500);

        // The section whose top has passed the reference line (just below the sticky nav)
        // is the one being read. Sections are in DOM order, so the last match wins.
        const line = el.scrollTop + 140;
        const sections = el.querySelectorAll<HTMLElement>('[data-section]');
        let current = NAV[0].id;
        sections.forEach((s) => {
            if (s.offsetTop <= line) current = s.dataset.section || current;
        });
        setActiveId(current);
    }, []);

    const scrollToSection = useCallback((id: string) => {
        const el = scrollRef.current;
        if (!el) return;
        const target = el.querySelector<HTMLElement>(`[data-section="${id}"]`);
        if (target) el.scrollTo({ top: Math.max(target.offsetTop - 56, 0), behavior: 'smooth' });
        setNavOpen(false);
    }, []);

    const scrollToTop = () =>
        scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });

    // OLD: reset progress/section/lightbox whenever `open` flipped true. Both
    // containers unmount their content while closed, so every open is a fresh mount.

    // Close the lightbox on Escape (before the modal itself closes).
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setLightbox(null);
            }
        };
        window.addEventListener('keydown', onKey, true);
        return () => window.removeEventListener('keydown', onKey, true);
    }, [lightbox]);

    const openLightbox = useCallback((src: string, alt: string) => setLightbox({ src, alt }), []);

    // A phone screenshot that opens in the lightbox on click. Memoized so its identity is
    // stable across the frequent scroll-driven re-renders (otherwise every shot remounts).
    const Shot = useMemo(() => {
        const Component: React.FC<{
            src: string;
            alt: string;
            caption: React.ReactNode;
            featured?: boolean;
        }> = ({ src, alt, caption, featured }) => (
            <figure className={`shot${featured ? ' featured' : ''}`}>
                <div
                    className="phone phone-sm"
                    onClick={() => openLightbox(src, alt)}
                    role="button"
                    aria-label={`Expand: ${alt}`}
                >
                    <div className="phone-notch" />
                    <img src={src} alt={alt} />
                </div>
                <figcaption>{caption}</figcaption>
            </figure>
        );
        return Component;
    }, [openLightbox]);

    return (
        <>
            {/* Always-visible close control, floating above the sticky guide header */}
            <IconButton
                onClick={onClose}
                aria-label="Close guide"
                size="small"
                sx={{
                    position: 'absolute',
                    top: 10,
                    right: 12,
                    zIndex: 1200,
                    bgcolor: 'rgba(255,255,255,0.9)',
                    boxShadow: 1,
                    '&:hover': { bgcolor: '#fff' },
                }}
            >
                <CloseRounded />
            </IconButton>

            <div className="zap-tour-guide" ref={scrollRef} onScroll={handleScroll}>
                {/* Progress bar */}
                <div className="scroll-progress" style={{ width: `${progress}%` }} />

                {/* Top nav */}
                <header className="topbar">
                    <button
                        className="brand"
                        onClick={scrollToTop}
                        style={{ background: 'none', border: 0, cursor: 'pointer', fontFamily: 'inherit' }}
                    >
                        <span className="bolt" aria-hidden="true">⚡</span>
                        <span className="brand-text" id="zap-tour-title">
                            ClanConnect <strong>Zap</strong>
                        </span>
                    </button>
                    <nav className={`topnav${navOpen ? ' show' : ''}`}>
                        {NAV.map((n) => (
                            <button
                                key={n.id}
                                className={activeId === n.id ? 'active' : ''}
                                onClick={() => scrollToSection(n.id)}
                            >
                                {n.label}
                            </button>
                        ))}
                    </nav>
                    <button
                        className="nav-toggle"
                        aria-label="Menu"
                        onClick={() => setNavOpen((v) => !v)}
                    >
                        ☰
                    </button>
                </header>

                {/* Hero */}
                <section className="hero">
                    <div className="hero-inner">
                        <div className="hero-copy">
                            <h1>
                                How to use <span className="grad">Zap</span>
                            </h1>
                            <p className="lead">
                                When someone comments a word like <code>LINK</code> on your Instagram post, Zap sends them
                                a message for you — automatically, day and night.
                            </p>
                            <p className="muted">Setting it up takes about 2 minutes.</p>
                            <div className="hero-cta">
                                <button className="btn btn-primary" onClick={() => scrollToSection('how')}>
                                    Show me how
                                </button>
                            </div>
                        </div>
                        <div className="hero-phone">
                            <div className="phone" onClick={() => openLightbox(sHome, 'Zap home screen')} role="button">
                                <div className="phone-notch" />
                                <img src={sHome} alt="Zap home screen" />
                            </div>
                            <div className="phone-glow" />
                        </div>
                    </div>
                </section>

                <main>
                    {/* How it works */}
                    <section className="chapter" data-section="how">
                        <div className="chapter-head">
                            <h2>How it works</h2>
                        </div>
                        <div className="flow">
                            <div className="flow-step"><span className="flow-num">1</span><p>Someone <strong>comments a word</strong> on your post, like <code>LINK</code>.</p></div>
                            <div className="flow-arrow">→</div>
                            <div className="flow-step"><span className="flow-num">2</span><p>Zap can <strong>reply under their comment</strong>, like “Check your DMs!”</p></div>
                            <div className="flow-arrow">→</div>
                            <div className="flow-step"><span className="flow-num">3</span><p>Zap sends them a <strong>private message</strong> with what they asked for.</p></div>
                        </div>
                    </section>

                    {/* Before you start */}
                    <section className="chapter alt" data-section="setup">
                        <div className="chapter-head">
                            <span className="kicker">One time only</span>
                            <h2>Before you start</h2>
                            <p className="chapter-sub">
                                Zap needs permission to send messages from your Instagram. If it isn’t on yet, the app will
                                ask you. Here’s how to turn it on in the Instagram app:
                            </p>
                        </div>
                        <div className="split-copy wide">
                            <ol className="steps">
                                <li>Go to your profile and tap the menu (☰).</li>
                                <li>Tap <strong>Settings and activity</strong> → <strong>Messages and story replies</strong>.</li>
                                <li>Tap <strong>Message controls</strong> → <strong>Connected tools</strong>.</li>
                                <li>Turn on <strong>“Allow access to messages”</strong>.</li>
                                <li>Come back to ClanConnect and tap <span className="pill pill-blue">It’s Done!</span></li>
                            </ol>
                            <div className="callout callout-info">
                                <span className="callout-icon">ℹ️</span>
                                <div>Your Instagram needs to be a <strong>Business</strong> or <strong>Creator</strong> account.</div>
                            </div>
                        </div>
                    </section>

                    {/* Start */}
                    <section className="chapter" data-section="start">
                        <div className="split">
                            <Shot src={sHome} alt="Zap home screen" caption="Tap ZAP Post/Reel to begin" />
                            <div className="split-copy">
                                <span className="kicker">Let’s go</span>
                                <h2>Open Zap</h2>
                                <p>Tap the <strong>⚡ Zap</strong> tab at the bottom of the app. Then tap <strong>ZAP Post/Reel</strong>{' '}
                                    (or <strong>ZAP Story</strong>, or <strong>ZAP Live</strong> if you’re going live).</p>
                            </div>
                        </div>
                    </section>

                    {/* Step 1 */}
                    <section className="chapter alt" data-section="step1">
                        <div className="split reverse">
                            <Shot src={sGoal} alt="Choose what you want this Zap to do" caption="Pick one of the four cards" />
                            <div className="split-copy">
                                <span className="kicker">Step 1 of 4</span>
                                <h2>Choose what you want</h2>
                                <p>Tap the card that fits, then tap <strong>Next</strong>.</p>
                                <ul className="ticks">
                                    <li><strong>Share my link</strong> — send people a link.</li>
                                    <li><strong>Earn affiliate revenue</strong> — send products people can buy.</li>
                                    <li><strong>Answer a common question</strong> — reply with an answer.</li>
                                    <li><strong>Collect leads</strong> — get people’s contact details.</li>
                                </ul>
                                <div className="callout callout-info">
                                    <span className="callout-icon">🙂</span>
                                    <div>Not sure? Start with <strong>Share my link</strong>. Cards with an <em>Upgrade</em> badge need a paid plan.</div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Step 2 */}
                    <section className="chapter" data-section="step2">
                        <div className="split">
                            <Shot src={sGrowth} alt="Grow your followers with this Zap" caption="Yes or No, then Next" />
                            <div className="split-copy">
                                <span className="kicker">Step 2 of 4</span>
                                <h2>Want more followers?</h2>
                                <ul className="ticks">
                                    <li><strong>Yes</strong> — people are asked to follow you before they get your message.</li>
                                    <li><strong>No</strong> — everyone who comments gets your message.</li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Step 3 */}
                    <section className="chapter alt" data-section="step3">
                        <div className="split reverse">
                            <Shot src={sPosts} alt="Posts selected" caption="Ticked posts show at the top" />
                            <div className="split-copy">
                                <span className="kicker">Step 3 of 4</span>
                                <h2>Choose your posts</h2>
                                <p>Tick the posts you want Zap to watch, then tap <strong>Next</strong>. Or choose
                                    <strong> Any Post</strong> to use it on all your posts, including new ones.</p>
                            </div>
                        </div>
                    </section>

                    {/* Step 4 */}
                    <section className="chapter" data-section="step4">
                        <div className="chapter-head">
                            <span className="kicker">Step 4 of 4</span>
                            <h2>Answer a few quick questions</h2>
                            <p className="chapter-sub">
                                One question per screen. Don’t know what to write? Tap one of the suggestions.
                            </p>
                        </div>
                        <div className="dual shot-row">
                            <Shot src={sWord} alt="Which word will people comment" caption="1. The word people comment (like LINK)" />
                            <Shot src={sReply} alt="Reply under their comment" caption="2. A reply under their comment (optional)" />
                            <Shot src={sWho} alt="Who should get your message" caption="3. Who gets it*" />
                            <Shot src={sCard} alt="The card they get first" caption={'4. The “please follow me” message*'} />
                            <Shot src={sMessage} alt="Which link should they get" caption="5. Your message and link" />
                        </div>
                        <p className="muted" style={{ textAlign: 'center' }}>
                            * You only see these if you said <strong>Yes</strong> to more followers in Step 2.
                        </p>
                    </section>

                    {/* Done */}
                    <section className="chapter alt" data-section="done">
                        <div className="split reverse">
                            <Shot src={sReview} alt="All set summary" caption="Your summary — then Go Live" />
                            <div className="split-copy">
                                <span className="kicker">Last screen</span>
                                <h2>Check it &amp; tap Go Live</h2>
                                <p>You’ll see a short summary of what will happen. Tap any line to change it. Then tap
                                    <strong> Go Live</strong>.</p>
                                <p>Want a different word to send something else? Tap <span className="pill">+ Add</span>.</p>
                            </div>
                        </div>
                        <div className="callout callout-success">
                            <span className="callout-icon">🎉</span>
                            <div><strong>That’s it — your Zap is on!</strong> You’ll find it under <strong>My Zaps</strong>. From there
                                you can turn it off, change it, or delete it anytime.</div>
                        </div>
                    </section>
                </main>


                <footer className="footer">
                    <p className="muted">Click any screenshot to see it bigger.</p>
                </footer>

                <button
                    className={`to-top${showToTop ? ' show' : ''}`}
                    aria-label="Back to top"
                    onClick={scrollToTop}
                >
                    ↑
                </button>
            </div>

            {/* Lightbox */}
            {lightbox && (
                <div className="zap-tour-lightbox" onClick={() => setLightbox(null)}>
                    <button className="lb-close" aria-label="Close" onClick={() => setLightbox(null)}>×</button>
                    <img src={lightbox.src} alt={lightbox.alt} onClick={(e) => e.stopPropagation()} />
                </div>
            )}
        </>
    );
};

const ZapTourModal: React.FC<Props> = ({ open, onClose }) => (
    <Modal open={open} onClose={onClose} aria-labelledby="zap-tour-title">
        <Box
            sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '95vw',
                maxWidth: 1100,
                height: '90vh',
                bgcolor: '#fff',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: 24,
                outline: 'none',
            }}
        >
            <ZapTourGuide onClose={onClose} />
        </Box>
    </Modal>
);

export default ZapTourModal;
